/**
 * NS-310: first-space onboarding progress, persisted in the browser only.
 *
 * There is no backend field for "this user still has to create their first space", so the stage
 * lives in localStorage. Consequences that are accepted, not oversights: clearing site data or
 * switching browsers skips the flow, and there is no recovery of historical bypasses. The record is
 * only ever a hint for the UI — nothing about access control depends on it.
 *
 * Scoped by user *and* team, because one browser can hold several accounts and one account can
 * belong to several workspaces; each combination onboards on its own.
 */

export enum OnboardingStage {
  WELCOME = 'WELCOME',
  TEMPLATE_SELECTION = 'TEMPLATE_SELECTION',
  COMPLETED = 'COMPLETED'
}

/** Bumped when the record shape changes; older or unknown versions are ignored, never migrated. */
export const ONBOARDING_RECORD_VERSION = 1

export const ONBOARDING_STORAGE_PREFIX = 'notch.onboarding.'

export type OnboardingRecord = {
  version: number
  stage: OnboardingStage
  /**
   * Template carried in from a shared-template signup link. Kept in the record so the selection
   * survives the query parameter being dropped from the URL (and a reload).
   */
  sharedTemplateId: string | null
  /**
   * Whether this onboarding session started from the post-register redirect. Persisted so funnel
   * analytics (trackOnboardingFlow2/3) survive Framework dropping the URL query param on mount.
   */
  fromRegisterFlow?: boolean
}

export function onboardingStorageKey(userId: string, teamId: string): string {
  return `${ONBOARDING_STORAGE_PREFIX}${userId}.${teamId}`
}

function isOnboardingStage(value: unknown): value is OnboardingStage {
  return Object.values(OnboardingStage).includes(value as OnboardingStage)
}

/**
 * Returns null for "no usable record" — absent, unparseable, wrong version or an unknown stage all
 * collapse to the same answer, since a missing record must never force a founder through onboarding.
 */
export function readOnboardingRecord(userId: string, teamId: string): OnboardingRecord | null {
  try {
    const raw = window.localStorage.getItem(onboardingStorageKey(userId, teamId))
    if (!raw) {
      return null
    }
    const parsed = JSON.parse(raw)
    if (!parsed || parsed.version !== ONBOARDING_RECORD_VERSION || !isOnboardingStage(parsed.stage)) {
      return null
    }
    return {
      version: ONBOARDING_RECORD_VERSION,
      stage: parsed.stage,
      sharedTemplateId: typeof parsed.sharedTemplateId === 'string' ? parsed.sharedTemplateId : null,
      fromRegisterFlow: parsed.fromRegisterFlow === true
    }
  } catch {
    // Malformed JSON, or storage blocked entirely (private mode, quota) — fall back to no record.
    return null
  }
}

export function writeOnboardingRecord(userId: string, teamId: string, record: OnboardingRecord): void {
  try {
    window.localStorage.setItem(onboardingStorageKey(userId, teamId), JSON.stringify(record))
  } catch {
    // Storage unavailable: the store keeps the stage in memory for this tab and the flow still
    // works, it just will not survive a reload. Better than breaking the app shell.
  }
}
